import {
  Breakpoints,
  IconStar,
  IconX,
  Tag,
  TagColor,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/router';
import { type FC, useRef } from 'react';

import { type GoalName } from '../../../__generated-gql-types__/globalTypes';
import { getReviewsPageUrl } from '../../../utils/routing';
import { getUrlObject } from '../../../utils/url';
import LoadingIndicator from '../../common/LoadingIndicator';
import Pagination from '../../common/Pagination';

import { MobileReviewsBreakdown } from './MobileReviewsBreakdown';
import { ReviewFilters } from './ReviewFilters';
import { ReviewsBreakdown } from './ReviewsBreakdown';
import { type ReviewsReviewSectionFragment } from './__generated-gql-types__/ReviewSection.generated';
import { Review } from './review/Review';

export const REVIEWS_PAGE_SIZE = 25;

interface ReviewSectionProps {
  reviewsByStarForGoal: number[];
  totalReviewsForGoal: number;
  totalReviewsForPagination: number;
  reviews: ReviewsReviewSectionFragment[];
  page: number;
  goalSearch: Possible<GoalName>;
  ratingSearch: Possible<number>;
  loading: boolean;
}
export const ReviewSection: FC<ReviewSectionProps> = ({
  reviewsByStarForGoal,
  totalReviewsForGoal,
  totalReviewsForPagination,
  reviews,
  page,
  goalSearch,
  ratingSearch,
  loading,
}) => {
  const router = useRouter();
  const isMd = useMediaQuery(Breakpoints.MD);
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <section className="mx-auto w-full sm:max-w-screen-2xl">
      <div className="flex flex-col gap-4 border-b border-leland-gray-stroke px-4 py-6 sm:px-14 sm:py-8">
        <div className="lg:hidden">
          <MobileReviewsBreakdown
            reviewsByStar={reviewsByStarForGoal}
            totalReviews={totalReviewsForGoal}
            goalSearch={goalSearch}
            ratingSearch={ratingSearch}
          />
        </div>
        <ReviewFilters goalSearch={goalSearch} ratingSearch={ratingSearch} />
      </div>
      {!loading ? (
        <div className="flex gap-14 p-4 sm:p-14" ref={scrollRef}>
          <div className="hidden lg:block">
            <ReviewsBreakdown
              reviewsByStar={reviewsByStarForGoal}
              totalReviews={totalReviewsForGoal}
              goalSearch={goalSearch}
              ratingSearch={ratingSearch}
            />
          </div>

          <div className="w-full">
            {ratingSearch != null ? (
              <RatingIndicator
                rating={ratingSearch}
                onClose={() => router.push(getReviewsPageUrl(goalSearch, null))}
              />
            ) : null}
            <div className="divide-y">
              {reviews.map((review) => (
                <Review review={review} key={review.id} />
              ))}
            </div>
            <div className="mt-8 flex items-center justify-center lg:justify-start">
              <Pagination
                currentPage={page}
                scrollRoot={scrollRef}
                totalElements={totalReviewsForPagination}
                pageSize={REVIEWS_PAGE_SIZE}
                buildUrl={(page) => {
                  return getUrlObject(router.pathname, {
                    ...router.query,
                    page,
                  });
                }}
                maxPageItems={isMd ? 5 : 3}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex w-full flex-col justify-center">
          <LoadingIndicator iconClassName="size-20" />
        </div>
      )}
    </section>
  );
};

interface RatingIndicatorProps {
  rating: number;
  onClose: () => void;
}
const RatingIndicator: FC<RatingIndicatorProps> = ({ rating, onClose }) => {
  return (
    <div className="flex">
      <Tag
        CustomLeftIcon={({ iconClassName }) => (
          <IconStar className={`${iconClassName} text-leland-yellow`} />
        )}
        tagColor={TagColor.TRANSPARENT}
        text={`Showing only ${rating} star reviews`}
        CustomRightIcon={({ iconClassName }) => (
          <IconX
            className={`${iconClassName} cursor-pointer text-leland-gray-extra-light hover:text-leland-gray-light`}
            onClick={onClose}
          />
        )}
      />
    </div>
  );
};
